/**
 * Automatic CRUD script generator for AI Agent tools.
 * Generates ServiceNow server-side scripts based on ToolInputType configuration.
 */

import type { Shape } from '@servicenow/sdk-build-core'
import { isToolInputType } from '../tool-input-helper'
import { generateLookupScript } from './lookup-script-template'
import { generateCreateScript } from './create-script-template'
import { generateUpdateScript } from './update-script-template'
import { generateDeleteScript } from './delete-script-template'

/**
 * Extracts ToolInputType from a Shape
 */
function extractToolInputType(inputsShape: Shape | undefined): unknown {
    if (!inputsShape) {
        return null
    }

    const value = inputsShape.getValue()
    if (value && isToolInputType(value)) {
        return value
    }

    return null
}

/**
 * Main function to generate CRUD script based on tool configuration
 * @param inputsShape - The inputs shape from the tool configuration
 * @param toolType - The type of the tool (should be 'crud')
 * @returns Generated script string or empty string if not applicable
 */
export function generateCrudScript(inputsShape: Shape | undefined, toolType: string | undefined): string {
    // Only generate scripts for CRUD tools
    if (toolType !== 'crud' || !inputsShape) {
        return ''
    }

    // Extract ToolInputType from the shape
    const toolInput = extractToolInputType(inputsShape)
    if (!toolInput || typeof toolInput !== 'object') {
        return ''
    }

    // Extract operation name from ToolInputType
    const operation = (toolInput as Record<string, unknown>)['operationName'] as string | undefined
    if (!operation) {
        return ''
    }

    // Dispatch to appropriate template generator based on operation
    switch (operation) {
        case 'lookup':
            return generateLookupScript()
        case 'create':
            return generateCreateScript()
        case 'update':
            return generateUpdateScript()
        case 'delete':
            return generateDeleteScript()
        default:
            // Unknown operation - return empty string
            return ''
    }
}
